/**
 * Which end of the list newly arrived items land on: `top` under newest-first
 * and unanswered-first, `bottom` under oldest-first.
 */
export type NewEngagementDirection = 'top' | 'bottom'

/**
 * Where the "New {comments,mentions}" divider goes, so the newly arrived items
 * sit on the far side of it from the settled list:
 * - `top`: after the leading run of new items (new above, settled below).
 * - `bottom`: before the trailing run of new items (settled above, new below).
 *
 * Returns `-1` when nothing is new, or when every visible item is new and there
 * is nothing to separate them from. Only a contiguous run at the relevant end
 * counts; a new item elsewhere in the list renders inline without a divider,
 * which keeps the divider honest when the list is interleaved.
 */
export const getNewEngagementDividerIndex = (
  itemIds: readonly string[],
  newlyArrivedIds: ReadonlySet<string>,
  direction: NewEngagementDirection,
): number => {
  if (newlyArrivedIds.size === 0) return -1

  if (direction === 'top') {
    let leadingNewCount = 0
    while (
      leadingNewCount < itemIds.length &&
      newlyArrivedIds.has(itemIds[leadingNewCount])
    ) {
      leadingNewCount += 1
    }
    return leadingNewCount > 0 && leadingNewCount < itemIds.length
      ? leadingNewCount
      : -1
  }

  let trailingNewCount = 0
  while (
    trailingNewCount < itemIds.length &&
    newlyArrivedIds.has(itemIds[itemIds.length - 1 - trailingNewCount])
  ) {
    trailingNewCount += 1
  }
  return trailingNewCount > 0 && trailingNewCount < itemIds.length
    ? itemIds.length - trailingNewCount
    : -1
}
